import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';

const WizardContainer = styled.div`
  min-height: 100vh;
  background: ${({ primaryColor }) => `linear-gradient(135deg, ${primaryColor}08 0%, ${primaryColor}02 100%)`};
`;

const WizardHeader = styled.header`
  background: ${({ primaryColor }) => primaryColor || '#3b82f6'};
  color: white;
  padding: 24px;
  text-align: center;

  .logo {
    width: 64px;
    height: 64px;
    margin: 0 auto 16px;
    border-radius: 16px;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    font-weight: 700;
    color: ${({ primaryColor }) => primaryColor || '#3b82f6'};
  }

  h1 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 8px;
  }

  p {
    opacity: 0.9;
    font-size: 0.875rem;
  }
`;

const ProgressBar = styled.div`
  background: white;
  padding: 16px 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 100;
`;

const ProgressSteps = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  max-width: 800px;
  margin: 0 auto;
  overflow-x: auto;
`;

const ProgressStep = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
  cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'default')};

  ${({ status, primaryColor }) => {
    if (status === 'completed') {
      return `
        background: ${primaryColor}20;
        color: ${primaryColor};
      `;
    }
    if (status === 'current') {
      return `
        background: ${primaryColor};
        color: white;
      `;
    }
    return `
      background: #f3f4f6;
      color: #9ca3af;
    `;
  }}

  .step-number {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    background: ${({ status, primaryColor }) => 
      status === 'current' ? 'rgba(255,255,255,0.2)' : 
      status === 'completed' ? primaryColor : '#e5e7eb'};
    color: ${({ status }) => (status === 'completed' ? 'white' : 'inherit')};

    svg {
      width: 14px;
      height: 14px;
    }
  }

  .step-name {
    white-space: nowrap;
  }
`;

const WizardContent = styled.main`
  max-width: 800px;
  margin: 0 auto;
  padding: 32px 24px;
`;

const SectionCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid #f3f4f6;

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 4px;
  }

  p {
    color: #6b7280;
    font-size: 0.875rem;
  }

  .required-badge {
    display: inline-block;
    padding: 2px 8px;
    background: #fee2e2;
    color: #dc2626;
    font-size: 0.625rem;
    font-weight: 600;
    border-radius: 4px;
    text-transform: uppercase;
    margin-left: 8px;
  }
`;

const ItemsGrid = styled.div`
  padding: 24px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
`;

const ItemCard = styled.div`
  position: relative;
  border: 2px solid ${({ selected, primaryColor }) => (selected ? primaryColor : '#e5e7eb')};
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${({ selected, primaryColor }) => (selected ? `${primaryColor}08` : 'white')};

  &:hover {
    border-color: ${({ primaryColor }) => primaryColor};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .check-badge {
    position: absolute;
    top: -8px;
    right: -8px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${({ primaryColor }) => primaryColor};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: ${({ selected }) => (selected ? 1 : 0)};
    transform: scale(${({ selected }) => (selected ? 1 : 0.5)});
    transition: all 0.2s;

    svg {
      width: 14px;
      height: 14px;
    }
  }

  .popular-badge {
    position: absolute;
    top: 12px;
    left: 12px;
    padding: 2px 8px;
    background: #fef3c7;
    color: #d97706;
    font-size: 0.625rem;
    font-weight: 600;
    border-radius: 4px;
    text-transform: uppercase;
  }

  .item-icon {
    font-size: 2rem;
    margin-bottom: 12px;
    display: block;
  }

  .item-name {
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 4px;
  }

  .item-description {
    font-size: 0.75rem;
    color: #6b7280;
    margin-bottom: 12px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .item-price {
    font-size: 1.125rem;
    font-weight: 700;
    color: ${({ primaryColor }) => primaryColor};
  }
`;

const WizardFooter = styled.footer`
  background: white;
  border-top: 1px solid #e5e7eb;
  padding: 16px 24px;
  position: sticky;
  bottom: 0;
`;

const FooterContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PriceSummary = styled.div`
  .label {
    font-size: 0.75rem;
    color: #6b7280;
    text-transform: uppercase;
  }

  .price {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
  }

  .vat-note {
    font-size: 0.75rem;
    color: #9ca3af;
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &.primary {
    background: ${({ primaryColor }) => primaryColor || '#3b82f6'};
    color: white;
    border: none;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px ${({ primaryColor }) => `${primaryColor || '#3b82f6'}40`};
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &.secondary {
    background: #f3f4f6;
    color: #374151;
    border: none;

    &:hover {
      background: #e5e7eb;
    }
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

// Summary Section
const SummarySection = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-bottom: 24px;
`;

const SummaryHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid #f3f4f6;

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
  }
`;

const SummaryContent = styled.div`
  padding: 24px;
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }

  .name {
    color: #374151;
  }

  .price {
    font-weight: 600;
    color: #1f2937;
  }
`;

const SummaryTotal = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 16px;
  margin-top: 16px;
  border-top: 2px solid #e5e7eb;

  .label {
    font-weight: 600;
    color: #1f2937;
  }

  .total {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${({ primaryColor }) => primaryColor || '#3b82f6'};
  }
`;

// Contact Form
const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  &.full-width {
    grid-column: 1 / -1;
  }

  label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 6px;
  }

  input, textarea {
    width: 100%;
    padding: 12px 16px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 0.875rem;

    &:focus {
      outline: none;
      border-color: ${({ primaryColor }) => primaryColor || '#3b82f6'};
      box-shadow: 0 0 0 3px ${({ primaryColor }) => `${primaryColor || '#3b82f6'}20`};
    }
  }

  textarea {
    min-height: 100px;
    resize: vertical;
  }
`;

const ErrorMessage = styled.p`
  color: #dc2626;
  font-size: 0.75rem;
  margin-top: 4px;
`;

// Success Screen
const SuccessScreen = styled.div`
  text-align: center;
  padding: 60px 24px;

  .icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: #dcfce7;
    color: #16a34a;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 24px;

    svg {
      width: 40px;
      height: 40px;
    }
  }

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 12px;
  }

  p {
    color: #6b7280;
    margin-bottom: 24px;
  }

  .order-number {
    display: inline-block;
    padding: 8px 16px;
    background: #f3f4f6;
    border-radius: 8px;
    font-family: monospace;
    font-size: 1.125rem;
    color: #374151;
  }
`;

const LoadingScreen = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  
  .spinner {
    width: 48px;
    height: 48px;
    border: 3px solid #e5e7eb;
    border-top-color: ${({ primaryColor }) => primaryColor || '#3b82f6'};
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  p {
    color: #6b7280;
  }
`;

const ErrorScreen = styled.div`
  text-align: center;
  padding: 60px 24px;

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #dc2626;
    margin-bottom: 12px;
  }

  p {
    color: #6b7280;
  }
`;

const PublicConfigurator = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [configurator, setConfigurator] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [selections, setSelections] = useState({});
  const [pricing, setPricing] = useState({ subtotal: 0, vat: 0, total: 0, items: [] });
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    address: '',
    city: '',
    postal_code: '',
    country: '',
    vat_id: '',
    notes: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  
  // Embed mode detection
  const isEmbedded = searchParams.get('embed') === 'true';
  const widgetId = searchParams.get('widgetId');
  const embedTheme = searchParams.get('theme') || 'light';
  const showPrices = searchParams.get('showPrices') !== 'false';

  // Post message to parent window (for embed mode)
  const postMessageToParent = useCallback((type, data = {}) => {
    if (isEmbedded && window.parent !== window) {
      window.parent.postMessage({
        type,
        widgetId,
        ...data
      }, '*');
    }
  }, [isEmbedded, widgetId]);

  // Listen for messages from parent window
  useEffect(() => {
    if (!isEmbedded) return;
    
    const handleMessage = (event) => {
      const data = event.data;
      if (!data || data.widgetId !== widgetId) return;
      
      switch (data.type) {
        case 'magelan:goToStep':
          if (data.data?.step !== undefined) {
            setCurrentStep(data.data.step);
          }
          break;
        case 'magelan:reset':
          setSelections({});
          setCurrentStep(0);
          setOrderResult(null);
          break;
        case 'magelan:getSelections':
          postMessageToParent('magelan:selections', { selections });
          break;
        case 'magelan:setSelections':
          if (data.data?.selections) {
            setSelections(data.data.selections);
          }
          break;
        case 'magelan:setCustomerInfo':
          setContactForm(prev => ({ ...prev, ...data.data }));
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isEmbedded, widgetId, selections, postMessageToParent]);

  // Notify parent when ready
  useEffect(() => {
    if (!loading && configurator && isEmbedded) {
      postMessageToParent('magelan:ready');
    }
  }, [loading, configurator, isEmbedded, postMessageToParent]);

  // Notify parent of step changes
  useEffect(() => {
    if (isEmbedded) {
      postMessageToParent('magelan:navigation', { step: currentStep });
    }
  }, [currentStep, isEmbedded, postMessageToParent]);

  // Auto-resize for embed mode
  useEffect(() => {
    if (!isEmbedded) return;
    
    const sendHeight = () => {
      const height = document.documentElement.scrollHeight;
      postMessageToParent('magelan:resize', { height });
    };
    
    // Send initial height
    sendHeight();
    
    // Observe for height changes
    const observer = new ResizeObserver(sendHeight);
    observer.observe(document.body);
    
    return () => observer.disconnect();
  }, [isEmbedded, postMessageToParent]);

  useEffect(() => {
    loadConfigurator();
  }, [slug]);

  useEffect(() => {
    if (configurator && Object.keys(selections).length > 0) {
      calculatePrice();
    }
  }, [selections]);

  const loadConfigurator = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/public/configurators/${slug}`);
      setConfigurator(response.data);
      
      // Initialize selections with default items
      const initialSelections = {};
      response.data.sections?.forEach((section) => {
        const defaultItems = section.items?.filter((item) => item.is_default) || [];
        if (defaultItems.length > 0) {
          if (section.selection_type === 'multiple') {
            initialSelections[section.id] = defaultItems.map((item) => item.id);
          } else {
            initialSelections[section.id] = defaultItems[0]?.id;
          }
        }
      });
      setSelections(initialSelections);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load configurator');
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = async () => {
    try {
      const selectedIds = [];
      Object.values(selections).forEach((value) => {
        if (Array.isArray(value)) {
          selectedIds.push(...value);
        } else if (value) {
          selectedIds.push(value);
        }
      });

      const response = await api.post(`/public/configurators/${slug}/calculate`, {
        selections: selectedIds,
      });
      setPricing(response.data);
    } catch (err) {
      console.error('Error calculating price:', err);
    }
  };

  const handleItemSelect = (sectionId, itemId, selectionType) => {
    setSelections((prev) => {
      if (selectionType === 'multiple') {
        const current = prev[sectionId] || [];
        if (current.includes(itemId)) {
          return { ...prev, [sectionId]: current.filter((id) => id !== itemId) };
        }
        return { ...prev, [sectionId]: [...current, itemId] };
      }
      return { ...prev, [sectionId]: itemId };
    });
  };

  const isItemSelected = (sectionId, itemId, selectionType) => {
    if (selectionType === 'multiple') {
      return (selections[sectionId] || []).includes(itemId);
    }
    return selections[sectionId] === itemId;
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const validateContact = () => {
    const errors = {};
    if (!contactForm.email) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(contactForm.email)) errors.email = 'Invalid email format';
    if (!contactForm.name) errors.name = 'Name is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateContact()) return;

    try {
      setSubmitting(true);
      const selectedIds = [];
      Object.values(selections).forEach((value) => {
        if (Array.isArray(value)) {
          selectedIds.push(...value);
        } else if (value) {
          selectedIds.push(value);
        }
      });

      const response = await api.post(`/public/configurators/${slug}/submit`, {
        selections: selectedIds,
        contact: contactForm,
        notes: contactForm.notes,
      });
      setOrderResult(response.data);
      
      // Notify parent window of successful submission (for embed mode)
      if (isEmbedded) {
        postMessageToParent('magelan:complete', { order: response.data });
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to submit order';
      setError(errorMsg);
      
      // Notify parent window of error (for embed mode)
      if (isEmbedded) {
        postMessageToParent('magelan:error', { error: errorMsg });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <WizardContainer>
        <LoadingScreen>
          <div className="spinner" />
          <p>Loading configurator...</p>
        </LoadingScreen>
      </WizardContainer>
    );
  }

  if (error) {
    return (
      <WizardContainer>
        <ErrorScreen>
          <h2>Oops!</h2>
          <p>{error}</p>
        </ErrorScreen>
      </WizardContainer>
    );
  }

  if (orderResult) {
    return (
      <WizardContainer primaryColor={configurator.primary_color}>
        <WizardHeader primaryColor={configurator.primary_color}>
          <div className="logo">
            {configurator.logo_url ? (
              <img src={configurator.logo_url} alt={configurator.name} />
            ) : (
              configurator.name.substring(0, 2).toUpperCase()
            )}
          </div>
          <h1>{configurator.name}</h1>
        </WizardHeader>
        <WizardContent>
          <SummarySection>
            <SuccessScreen>
              <div className="icon">
                <CheckIcon />
              </div>
              <h2>Thank You!</h2>
              <p>Your configuration has been submitted successfully.</p>
              <div className="order-number">Order #{orderResult.order_id}</div>
            </SuccessScreen>
          </SummarySection>
        </WizardContent>
      </WizardContainer>
    );
  }

  const steps = [
    ...(configurator.sections || []).map((section) => ({
      id: section.id,
      name: section.name,
      type: 'section',
      data: section,
    })),
    { id: 'summary', name: 'Summary', type: 'summary' },
    { id: 'contact', name: 'Contact', type: 'contact' },
  ];

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isSummaryStep = currentStepData?.type === 'summary';
  const isContactStep = currentStepData?.type === 'contact';

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: configurator.currency || 'EUR',
    }).format(price);
  };

  return (
    <WizardContainer primaryColor={configurator.primary_color}>
      <WizardHeader primaryColor={configurator.primary_color}>
        <div className="logo">
          {configurator.logo_url ? (
            <img src={configurator.logo_url} alt={configurator.name} />
          ) : (
            configurator.name.substring(0, 2).toUpperCase()
          )}
        </div>
        <h1>{configurator.name}</h1>
        <p>{configurator.description}</p>
      </WizardHeader>

      <ProgressBar>
        <ProgressSteps>
          {steps.map((step, index) => (
            <ProgressStep
              key={step.id}
              status={index < currentStep ? 'completed' : index === currentStep ? 'current' : 'pending'}
              primaryColor={configurator.primary_color}
              isClickable={index < currentStep}
              onClick={() => index < currentStep && setCurrentStep(index)}
            >
              <div className="step-number">
                {index < currentStep ? <CheckIcon /> : index + 1}
              </div>
              <span className="step-name">{step.name}</span>
            </ProgressStep>
          ))}
        </ProgressSteps>
      </ProgressBar>

      <WizardContent>
        {currentStepData?.type === 'section' && (
          <SectionCard>
            <SectionHeader>
              <h2>
                {currentStepData.data.name}
                {currentStepData.data.is_required && (
                  <span className="required-badge">Required</span>
                )}
              </h2>
              {currentStepData.data.description && (
                <p>{currentStepData.data.description}</p>
              )}
            </SectionHeader>
            <ItemsGrid>
              {currentStepData.data.items?.map((item) => (
                <ItemCard
                  key={item.id}
                  selected={isItemSelected(currentStepData.data.id, item.id, currentStepData.data.selection_type)}
                  primaryColor={configurator.primary_color}
                  onClick={() => handleItemSelect(currentStepData.data.id, item.id, currentStepData.data.selection_type)}
                >
                  <div className="check-badge">
                    <CheckIcon />
                  </div>
                  {item.is_popular && <span className="popular-badge">Popular</span>}
                  {item.icon && <span className="item-icon">{item.icon}</span>}
                  <div className="item-name">{item.name}</div>
                  {item.description && (
                    <div className="item-description">{item.description}</div>
                  )}
                  {configurator.show_prices && (
                    <div className="item-price">{formatPrice(item.price)}</div>
                  )}
                </ItemCard>
              ))}
            </ItemsGrid>
          </SectionCard>
        )}

        {isSummaryStep && (
          <SummarySection>
            <SummaryHeader>
              <h2>Your Selection Summary</h2>
            </SummaryHeader>
            <SummaryContent>
              {pricing.items.map((item) => (
                <SummaryItem key={item.id}>
                  <span className="name">{item.name}</span>
                  <span className="price">{formatPrice(item.price)}</span>
                </SummaryItem>
              ))}
              {pricing.items.length === 0 && (
                <p style={{ color: '#9ca3af', textAlign: 'center', padding: '20px' }}>
                  No items selected
                </p>
              )}
              <SummaryTotal primaryColor={configurator.primary_color}>
                <span className="label">Subtotal</span>
                <span>{formatPrice(pricing.subtotal)}</span>
              </SummaryTotal>
              {pricing.vat > 0 && (
                <SummaryItem>
                  <span className="name">VAT ({pricing.vat_rate}%)</span>
                  <span className="price">{formatPrice(pricing.vat)}</span>
                </SummaryItem>
              )}
              <SummaryTotal primaryColor={configurator.primary_color}>
                <span className="label">Total</span>
                <span className="total">{formatPrice(pricing.total)}</span>
              </SummaryTotal>
            </SummaryContent>
          </SummarySection>
        )}

        {isContactStep && (
          <SummarySection>
            <SummaryHeader>
              <h2>Contact Information</h2>
            </SummaryHeader>
            <SummaryContent>
              <FormGrid>
                <FormGroup primaryColor={configurator.primary_color}>
                  <label>Full Name *</label>
                  <input
                    type="text"
                    value={contactForm.name}
                    onChange={(e) => setContactForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="John Doe"
                  />
                  {formErrors.name && <ErrorMessage>{formErrors.name}</ErrorMessage>}
                </FormGroup>
                <FormGroup primaryColor={configurator.primary_color}>
                  <label>Email *</label>
                  <input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm((p) => ({ ...p, email: e.target.value }))}
                    placeholder="john@example.com"
                  />
                  {formErrors.email && <ErrorMessage>{formErrors.email}</ErrorMessage>}
                </FormGroup>
                <FormGroup primaryColor={configurator.primary_color}>
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm((p) => ({ ...p, phone: e.target.value }))}
                    placeholder="+1 234 567 890"
                  />
                </FormGroup>
                <FormGroup primaryColor={configurator.primary_color}>
                  <label>Company</label>
                  <input
                    type="text"
                    value={contactForm.company}
                    onChange={(e) => setContactForm((p) => ({ ...p, company: e.target.value }))}
                    placeholder="Acme Inc."
                  />
                </FormGroup>
                <FormGroup primaryColor={configurator.primary_color}>
                  <label>Address</label>
                  <input
                    type="text"
                    value={contactForm.address}
                    onChange={(e) => setContactForm((p) => ({ ...p, address: e.target.value }))}
                    placeholder="123 Main Street"
                  />
                </FormGroup>
                <FormGroup primaryColor={configurator.primary_color}>
                  <label>City</label>
                  <input
                    type="text"
                    value={contactForm.city}
                    onChange={(e) => setContactForm((p) => ({ ...p, city: e.target.value }))}
                    placeholder="New York"
                  />
                </FormGroup>
                <FormGroup primaryColor={configurator.primary_color}>
                  <label>VAT ID</label>
                  <input
                    type="text"
                    value={contactForm.vat_id}
                    onChange={(e) => setContactForm((p) => ({ ...p, vat_id: e.target.value }))}
                    placeholder="EU123456789"
                  />
                </FormGroup>
                <FormGroup primaryColor={configurator.primary_color}>
                  <label>Country</label>
                  <input
                    type="text"
                    value={contactForm.country}
                    onChange={(e) => setContactForm((p) => ({ ...p, country: e.target.value }))}
                    placeholder="United States"
                  />
                </FormGroup>
                <FormGroup primaryColor={configurator.primary_color} className="full-width">
                  <label>Notes</label>
                  <textarea
                    value={contactForm.notes}
                    onChange={(e) => setContactForm((p) => ({ ...p, notes: e.target.value }))}
                    placeholder="Any additional notes or requirements..."
                  />
                </FormGroup>
              </FormGrid>
            </SummaryContent>
          </SummarySection>
        )}
      </WizardContent>

      <WizardFooter>
        <FooterContent>
          <PriceSummary>
            <div className="label">Estimated Total</div>
            <div className="price">{formatPrice(pricing.total)}</div>
            <div className="vat-note">incl. {configurator.vat_rate}% VAT</div>
          </PriceSummary>
          <NavigationButtons>
            {currentStep > 0 && (
              <Button className="secondary" onClick={handlePrev}>
                <ChevronLeftIcon />
                Back
              </Button>
            )}
            {!isLastStep ? (
              <Button className="primary" primaryColor={configurator.primary_color} onClick={handleNext}>
                Next
                <ChevronRightIcon />
              </Button>
            ) : (
              <Button
                className="primary"
                primaryColor={configurator.primary_color}
                onClick={handleSubmit}
                disabled={submitting}
              >
                <ShoppingCartIcon />
                {submitting ? 'Submitting...' : 'Submit Order'}
              </Button>
            )}
          </NavigationButtons>
        </FooterContent>
      </WizardFooter>
    </WizardContainer>
  );
};

export default PublicConfigurator;
